import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { accuracyEnum } from '../../common/models/accuracy.enum';
import { usePrevious } from '../../common/react-helpers';
import { Building, Site } from '../../confi-state';
import { workspaceEnum } from '../../models/workspace.enum';
import { ServiceManager } from '../../services/service-manager';
import { PropsCorridor } from '../BuildingConf/Corridor/models/props-config';
import { PropsHybrid } from '../BuildingConf/Hybrid/models/props-config';
import { buildingType } from '../BuildingConf/models/building-type.enum';
import { UnitsAmenities } from '../BuildingConf/models/units-amenities';
import { PropsTower } from '../BuildingConf/Tower/models/props-config';
import { FormControl, InputLabel, Select, MenuItem, Button, FormControlLabel, Checkbox, Typography, IconButton } from '@mui/material';
import styles from './Babylon.module.css';
import { nlCartiqoLayersEnum } from '../../common/models/nl-cartiqo-layers.enum';
import { Point3 } from '../../common/models/point.type';
import EyeOpenIcon from '../Common/Icons/EyeOpenIcon/EyeOpenIcon';
import EyeShutIcon from '../Common/Icons/EyeShutIcon/EyeShutIcon';
import { AutoFixHigh, Mail } from '@mui/icons-material';
import { explanationIds, UserData } from '../../common/authentication/models/user';
import AppContactDialog from '../Common/AppContactDialog/AppContactDialog';
import { ContactPerson } from '../../environment';

type CombinedProps = PropsHybrid | PropsCorridor | PropsTower;

enum visibilityEnum {
    buildings = 'buildings',
    trees = 'trees',
    grid = 'grid',
    map = 'map',
    underlay = 'underlay',
}

let activePromiseIds = [];
const typesToPick = [nlCartiqoLayersEnum.buildings, nlCartiqoLayersEnum.trees];

interface BabylonProps {
    currentBuilding: Building,
    currentSite: Site,
    contactPerson: ContactPerson,
    user: UserData,
    pickedContextItem: { id: number | string, type: nlCartiqoLayersEnum },
    section: any,
    accuracy: accuracyEnum,
    workspace: workspaceEnum,
    changingContextVisibility: boolean,
    drawingParcel: boolean,
    pickingParcels: boolean,
    editingUnderlay: boolean,
    onChangeBuilding: (projectId: number | string, siteId: number | string, building: Building, saveToDb?: boolean) => void,
    onChangeSite: (projectId: number | string, site: Site) => void,
    onChangeAccuracy: (accuracy: accuracyEnum) => void,
    setChangingContextVisibility: (vis: boolean) => void
}

const Babylon: FC<BabylonProps> = ({
    currentBuilding,
    currentSite,
    contactPerson,
    user,
    pickedContextItem,
    section,
    accuracy,
    workspace,
    changingContextVisibility,
    drawingParcel,
    pickingParcels,
    editingUnderlay,
    onChangeBuilding,
    onChangeSite,
    onChangeAccuracy,
    setChangingContextVisibility
}) => {

    const canvasRef = useRef(undefined);
    const previousBuilding = usePrevious<Building>(currentBuilding);
    const [openAppContactDialog, setOpenAppContactDialog] = useState<boolean>(false);

    useEffect(() => {
        ServiceManager.babylonService.hybridBabylonService.setSection(section);
        ServiceManager.babylonService.corridorBabylonService.setSection(section);
        ServiceManager.babylonService.towerBabylonService.setSection(section);
        ServiceManager.babylonService.rowhouseBabylonService.setSection(section);
    }, [section])

    useEffect(() => {
        if (workspace === workspaceEnum.babylon) {
            // if location changed, we dont need to rerender
            const locationOrRotationSameAsBefore = () => {
                let result = true;
                if (currentBuilding && previousBuilding) {
                    const currentLocation = currentBuilding.locationInSite;
                    const previusLocation = previousBuilding.locationInSite;
                    result = currentLocation.position[0] === previusLocation.position[0] && currentLocation.position[1] === previusLocation.position[1] && currentLocation.position[2] === previusLocation.position[2] &&
                        currentLocation.rotation[0] === previusLocation.rotation[0] && currentLocation.rotation[1] === previusLocation.rotation[1] && currentLocation.rotation[2] === previusLocation.rotation[2]
                }

                return result;
            }

            const buildingsHaveChanged = () => {
                // we search for first indication that building has changed fundamentally to rerender
                let result = false;
                if (!previousBuilding || !previousBuilding.props) {
                    result = true;
                } else if ((previousBuilding.props as CombinedProps).customPlinthHeight !== (currentBuilding.props as CombinedProps).customPlinthHeight ||
                    (previousBuilding.props as CombinedProps).customPlinthWidth !== (currentBuilding.props as CombinedProps).customPlinthWidth) {
                    result = true;
                } else if ((previousBuilding.props as any).facade !== (currentBuilding.props as any).facade) {
                    result = true;
                }
                else if (previousBuilding.type !== currentBuilding.type) {
                    result = true;
                }
                else {
                    type ColType = {
                        type: string,
                        unitsAndAmenities: UnitsAmenities[]
                    };
                    let prevColumns = [];
                    if (previousBuilding.props.parts && previousBuilding.props.parts.length > 0) {
                        prevColumns = ServiceManager.babylonService.convertPartsToColumns<ColType>(previousBuilding.props.parts as any);
                    }
                    const currentColumns = ServiceManager.babylonService.convertPartsToColumns<ColType>(currentBuilding.props.parts as any);

                    if (prevColumns.length !== currentColumns.length) {
                        result = true;
                    } else if (prevColumns.length === 0 && currentColumns.length === 0) {
                        result = false;
                    }
                    else {
                        for (let i = 0; i < prevColumns.length; i++) {
                            const prevCol = prevColumns[i];
                            const curCol = currentColumns[i];
                            if (!prevCol || prevCol.type !== curCol.type) {
                                // if types are different, definitely rerender
                                result = true;
                                break;
                            } else if (prevCol.unitsAndAmenities && prevCol.unitsAndAmenities.length !== curCol.unitsAndAmenities.length) {
                                // this usually simply means that building height changed, thus unit and amenity number changed and rerendering needs to happen
                                result = true;
                                break;
                            } else if (prevCol.unitsAndAmenities) {
                                for (let j = 0; j < prevCol.unitsAndAmenities.length; j++) {
                                    const prevUA = prevCol.unitsAndAmenities[j];
                                    const curUA = curCol.unitsAndAmenities[j];
                                    if (prevUA.units && prevUA.units.length > 0) {
                                        for (let ui = 0; ui < prevUA.units.length; ui++) {
                                            const prevUnits = prevUA.units[ui];
                                            const curUnits = curUA.units[ui];
                                            if (prevUnits.unitType !== curUnits.unitType) {
                                                result = true;
                                                break;
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
                return result;
            }

            // Rendering buildings is an expensive process and these gates make sure that re-rendering happens only when building really changes
            if (
                currentBuilding &&
                currentBuilding.props &&
                currentBuilding.props.parts &&
                currentBuilding.props.parts.length > 0 &&
                section &&
                (locationOrRotationSameAsBefore())

            ) {

                if (!activePromiseIds.some(pid => pid === currentBuilding.id)) {
                    ServiceManager.babylonService.sceneManager.enabledRendering = true;
                    ServiceManager.babylonService.sceneManager.modelLoaderService.cleanMeshInstancesWithGroupId(currentBuilding.id);
                    if (currentBuilding.type === buildingType.hybrid) {
                        ServiceManager.babylonService.hybridBabylonService.setSection(section);
                    } else if (currentBuilding.type === buildingType.doubleLoadedCorridor) {
                        ServiceManager.babylonService.corridorBabylonService.setSection(section);
                    }

                    const promise = ServiceManager.babylonService.renderBuildingTo3D(currentBuilding, accuracy);
                    // this prevents from calling rerender if rerender was already initiated
                    activePromiseIds.push(currentBuilding.id);
                    if (buildingsHaveChanged()) {
                        console.log('updating');
                        promise.then(() => {
                            activePromiseIds = [...activePromiseIds.filter(pid => pid !== currentBuilding.id)];
                            ServiceManager.babylonService.sceneManager.enabledRendering = true;

                            const dim = ServiceManager.babylonService.sceneManager.modelLoaderService.getGizmoBoxDimensions(currentBuilding.id);
                            const roundToTwo = (num) => {
                                return Math.round(num * 100) / 100;
                            }
                            const size = [roundToTwo(dim[0]), roundToTwo(dim[1]), roundToTwo(dim[2])] as Point3;
                            const initialDimensions = (dimensions: Point3): boolean => {
                                return dimensions[0] === 1 && dimensions[1] === 1 && dimensions[2] === 1;
                            }
                            const mismatchInDimensions = (dimensions: Point3): boolean => {
                                return size[0] !== currentBuilding.props.buildingLength || size[1] !== currentBuilding.props.buildingHeight || size[2] !== currentBuilding.props.buildingWidth;
                            }
                            if (!initialDimensions(size) && mismatchInDimensions(size)) {
                                // The final chagne to DB is made here
                                onChangeBuilding(currentBuilding.projectId, currentBuilding.siteId, { ...currentBuilding, props: { ...currentBuilding.props, buildingLength: size[0], buildingHeight: size[1], buildingWidth: size[2] } })
                            } else {
                                onChangeBuilding(currentBuilding.projectId, currentBuilding.siteId, { ...currentBuilding, props: { ...currentBuilding.props } });
                            }
                        })
                    } else {
                        console.log('rendering');
                        promise.then(() => {
                            activePromiseIds = [...activePromiseIds.filter(pid => pid !== currentBuilding.id)];
                            ServiceManager.babylonService.sceneManager.enabledRendering = true;
                            if (currentBuilding && currentBuilding.refined) {
                                ServiceManager.sceneManager.modelLoaderService.disableBoundingBoxGizmo(currentBuilding.id);
                            } else if (currentBuilding && !currentBuilding.refined) {
                                ServiceManager.sceneManager.modelLoaderService.enableBoundingBoxGizmo(currentBuilding.id);
                            }
                        })
                    }
                }
            }
        }
    }, [currentBuilding, section, accuracy, workspace, previousBuilding, onChangeBuilding])

    useEffect(() => {
        if (!ServiceManager.sceneManager.isInitialised()) {
            ServiceManager.sceneManager.initScene(canvasRef.current as any);
        }
    }, [canvasRef])

    useEffect(() => {
        if (changingContextVisibility && pickedContextItem) {
            const site = { ...currentSite, context: currentSite.context ? currentSite.context.map(x => ({ ...x })) : [] };
            let contextItem = site.context.find(ci => ci.id === pickedContextItem.id);
            if (contextItem && typesToPick.some(t => t === contextItem.type)) {
                contextItem.visible = !contextItem.visible;
                if (contextItem.visible) {
                    ServiceManager.sceneManager.contextService.makeContextItemVisible(pickedContextItem.id);
                } else {
                    ServiceManager.sceneManager.contextService.setToInvisibleThroughAlphaToContextItem(pickedContextItem.id, 0.2);
                }
                onChangeSite(site.projectId, site);
            } else if (typesToPick.some(t => t === pickedContextItem.type)) {
                contextItem = {
                    id: pickedContextItem.id,
                    type: (pickedContextItem as any).type,
                    visible: false
                }
                ServiceManager.sceneManager.contextService.setToInvisibleThroughAlphaToContextItem(pickedContextItem.id, 0.2);
                site.context.push(contextItem);
                onChangeSite(site.projectId, site);
            }
        }
        // eslint-disable-next-line
    }, [pickedContextItem])

    const changeVisibility = useCallback((hideItem: visibilityEnum) => {
        if (currentSite) {
            const cs = { ...currentSite };
            if (!cs.options) {
                cs.options = { buildingsVisible: true, treesVisible: true, mapVisible: true, gridVisible: true, underlayVisible: true };
            }

            const siteContextItems = cs.context;
            const siteOptions = cs.options;

            let changed = false;
            switch (hideItem) {
                case visibilityEnum.buildings:
                    cs.options.buildingsVisible = !cs.options.buildingsVisible;
                    if (cs.options.buildingsVisible) {
                        ServiceManager.sceneManager.contextService.showContextTypes([nlCartiqoLayersEnum.buildings]);

                        if (siteContextItems && (siteOptions ? siteOptions.buildingsVisible : true)) {
                            siteContextItems.forEach(sci => {
                                if (sci.type === nlCartiqoLayersEnum.buildings && !sci.visible) {
                                    ServiceManager.sceneManager.contextService.hideContextItem(sci.id);
                                }
                            });
                        }
                    } else {
                        ServiceManager.sceneManager.contextService.hideContextTypes([nlCartiqoLayersEnum.buildings]);
                    }
                    changed = true;
                    break;
                case visibilityEnum.trees:
                    cs.options.treesVisible = !cs.options.treesVisible;
                    if (cs.options.treesVisible) {
                        ServiceManager.sceneManager.contextService.showContextTypes([nlCartiqoLayersEnum.trees]);
                        if (siteContextItems && (siteOptions ? siteOptions.buildingsVisible : true)) {
                            siteContextItems.forEach(sci => {
                                if (sci.type === nlCartiqoLayersEnum.trees && !sci.visible) {
                                    ServiceManager.sceneManager.contextService.hideContextItem(sci.id);
                                }
                            });
                        }
                    } else {
                        ServiceManager.sceneManager.contextService.hideContextTypes([nlCartiqoLayersEnum.trees]);
                    }
                    changed = true;
                    break;
                case visibilityEnum.map:
                    cs.options.mapVisible = !cs.options.mapVisible;
                    if (cs.options.mapVisible) {
                        ServiceManager.sceneManager.tileService.showTiledGround();
                    } else {
                        ServiceManager.sceneManager.tileService.hideTiledGround();
                    }
                    changed = true;
                    break;
                case visibilityEnum.grid:
                    cs.options.gridVisible = !cs.options.gridVisible;
                    if (cs.options.gridVisible) {
                        ServiceManager.sceneManager.showGroundGrid();
                    } else {
                        ServiceManager.sceneManager.hideGroundGrid();
                    }
                    changed = true;
                    break;
                case visibilityEnum.underlay:
                    cs.options.underlayVisible = !cs.options.underlayVisible;
                    if (cs.options.underlayVisible) {
                        ServiceManager.sceneManager.imageTileService.showImageTile();
                    } else {
                        ServiceManager.sceneManager.imageTileService.hideImageTile();
                    }
                    changed = true;
                    break;
                default:
                    break;
            }

            if (changed) {
                onChangeSite(currentSite.projectId, { ...cs });
            }

        }
    }, [currentSite, onChangeSite])

    const handleContact = () => {
        // open email modal
        setOpenAppContactDialog(true);
    }

    const activateHiding = () => {
        let finalTypesToPick = [];
        const options = currentSite.options;
        if (options && options.buildingsVisible) {
            finalTypesToPick.push(nlCartiqoLayersEnum.buildings);
        }
        if (options && options.treesVisible) {
            finalTypesToPick.push(nlCartiqoLayersEnum.trees);
        }
        if (finalTypesToPick.length > 0) {
            ServiceManager.sceneManager.contextService.startHidingContextItems(finalTypesToPick);
            setChangingContextVisibility(true);
        }
    }

    const stopChangingVisibility = () => {
        ServiceManager.sceneManager.contextService.setAlphaContextItemsToInvisible(typesToPick);
        setChangingContextVisibility(false);
    }

    const shouldShowContextHide = () => {
        const options = currentSite?.options;
        return !options || options.buildingsVisible || options.treesVisible;
    }

    return (
        <div className={styles.Babylon} data-testid="Babylon">
            <div className={styles.panel}>
                {
                    (!drawingParcel && !pickingParcels && !editingUnderlay) &&
                    <>
                        <FormControl variant="standard" fullWidth>
                            <InputLabel id="project-selection-label">Weergave</InputLabel>
                            <Select
                                data-explanation-id={explanationIds.initialDisplay}
                                labelId="project-selection-label"
                                id="project-selection"
                                value={accuracy}
                                onChange={(event) => onChangeAccuracy(event.target.value as accuracyEnum)}
                                label="Project"
                            >
                                <MenuItem value={accuracyEnum.conceptual}>Conceptual</MenuItem>
                                <MenuItem value={accuracyEnum.accurate}>Accurate</MenuItem>
                                {/* <MenuItem value={accuracyEnum.presentation}>Presentation</MenuItem> */}
                            </Select>
                            {
                                changingContextVisibility ?
                                    <Button color="secondary" size="small" variant="outlined" onClick={stopChangingVisibility}>Klaar</Button> :
                                    <>
                                        {(currentSite) &&
                                            <>

                                                <>
                                                    {
                                                        currentSite.lon && currentSite.lat &&
                                                        <>
                                                            <FormControlLabel
                                                                label={<Typography variant="body2" color="textPrimary">Gebouwen</Typography>}
                                                                className={styles.labelOn3d}
                                                                control={
                                                                    <Checkbox
                                                                        checkedIcon={<EyeOpenIcon />}
                                                                        icon={<EyeShutIcon />}
                                                                        size="medium"
                                                                        checked={currentSite?.options ? currentSite.options.buildingsVisible : true}
                                                                        indeterminate={false}
                                                                        onChange={() => { changeVisibility(visibilityEnum.buildings) }}
                                                                    />
                                                                }
                                                            />
                                                            <FormControlLabel
                                                                label={<Typography variant="body2" color="textPrimary">Bomen</Typography>}
                                                                className={styles.labelOn3d}
                                                                control={
                                                                    <Checkbox
                                                                        checkedIcon={<EyeOpenIcon />}
                                                                        icon={<EyeShutIcon />}
                                                                        size="medium"
                                                                        checked={currentSite?.options ? currentSite.options.treesVisible : true}
                                                                        indeterminate={false}
                                                                        onChange={() => { changeVisibility(visibilityEnum.trees) }}
                                                                    />
                                                                }
                                                            />
                                                            <FormControlLabel
                                                                label={<Typography variant="body2" color="textPrimary">Map</Typography>}
                                                                control={
                                                                    <Checkbox
                                                                        checkedIcon={<EyeOpenIcon />}
                                                                        icon={<EyeShutIcon />}
                                                                        size="medium"
                                                                        checked={currentSite?.options ? currentSite.options.mapVisible : true}
                                                                        indeterminate={false}
                                                                        onChange={() => { changeVisibility(visibilityEnum.map) }}
                                                                    />
                                                                }
                                                            />
                                                        </>
                                                    }
                                                    <FormControlLabel
                                                        label={<Typography variant="body2" color="textPrimary">Grid</Typography>}
                                                        className={styles.labelOn3d}
                                                        control={
                                                            <Checkbox
                                                                checkedIcon={<EyeOpenIcon />}
                                                                icon={<EyeShutIcon />}
                                                                size="medium"
                                                                checked={currentSite?.options ? currentSite.options.gridVisible : true}
                                                                indeterminate={false}
                                                                onChange={() => { changeVisibility(visibilityEnum.grid) }}
                                                            />
                                                        }
                                                    />
                                                    {currentSite.underlayImage &&
                                                        <FormControlLabel
                                                            label={<Typography variant="body2" color="textPrimary">Achtergrond</Typography>}
                                                            className={styles.labelOn3d}
                                                            control={
                                                                <Checkbox
                                                                    checkedIcon={<EyeOpenIcon />}
                                                                    icon={<EyeShutIcon />}
                                                                    size="medium"
                                                                    checked={currentSite?.options ? currentSite.options.underlayVisible : true}
                                                                    indeterminate={false}
                                                                    onChange={() => { changeVisibility(visibilityEnum.underlay) }}
                                                                />
                                                            }
                                                        />
                                                    }
                                                </>
                                                {currentSite.lon && currentSite.lat && <Button color="primary" disabled={!shouldShowContextHide()} size="small" variant="outlined" onClick={activateHiding}><AutoFixHigh fontSize="small" />&nbsp;&nbsp;Verberg Object</Button>}
                                            </>}
                                    </>
                            }
                        </FormControl>
                    </>
                }
            </div>
            <canvas className={styles.babylon} ref={canvasRef} id="renderCanvas"></canvas>
            <div className={styles.actions}>
                <Button onClick={handleContact} className={styles.mail} variant="contained" color="primary" data-testid="contact-dialog" aria-label="Contact dialog open" component="span">
                    &nbsp;&nbsp;&nbsp;CONTACT&nbsp;&nbsp;&nbsp;
                </Button>
                {/* <IconButton onClick={handleContact} className={styles.mail} color="primary" data-testid="contact-dialog" aria-label="Contact dialog open" component="span">
                    <Mail />
                </IconButton> */}
                <AppContactDialog user={user} contactPerson={contactPerson} open={openAppContactDialog} setOpen={setOpenAppContactDialog} />
            </div>
        </div>
    )
};

export default Babylon;
